import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useDeepEqualMemo } from "utils/deepEqualMemo";

import {
  APIOrchidDriveDirectory,
  APIOrchidDriveFile,
  DriveEntryErrorById,
  OrchidDriveData,
  OrchidDriveUploadTask,
  orchidDriveUploadTaskFromAPI,
  OrchidDriveUploadTaskWithEntries,
  UsedMemberDrive,
  UsedNoteAttachment,
  UsedPatientChart,
} from "./types";
import {
  UploadFileGenericFunc,
  UploadFileMemberDriveFunc,
  UploadFilePatientChartFunc,
  UploadFileNoteAttachmentFunc,
  UploadFileContinueFunc,
  BatchEntryOpsFunc,
  UploadTaskAbortFunc,
} from "./components/orchidDriveProvider";
import {
  CreateDirectoryFunc,
  CreateDirectoryMemberDriveFunc,
  CreateDirectoryPatientChartFunc,
  CreateDirectoryNoteAttachmentFunc,
} from "./components/orchidDriveProvider";
import {
  OrchidDriveProvider,
  DownloadFilesFunc,
  MoveEntryFunc,
  DeleteEntryFunc,
} from "./components/orchidDriveProvider";
import {
  APIRequestResult,
  APIRequests,
  usePrivateAPIData,
  useFetchAPIError,
} from "contexts/api-data-provider";
import {
  AddPendingDriveEntriesFunc,
  UpdateAvailableDriveEntriesFunc,
  useOrchidDriveData,
} from "./components/orchidDriveData";
import {
  useMergedDriveErrorByEntryIds,
  useMergedOrchidDriveFiles,
} from "./components/utils";
import {
  EntriesNeedKeysFunc,
  OrchidDriveKeysByEntryId,
} from "./components/orchidDriveEntryKeys";
import { LocalUploadTaskStatus } from "./components/fileUploadProgress";
import { OrchidDriveDownloadProgress } from "./reducers/orchidDriveDownloadProgress";
import { useDownloadProgress } from "./components/downloadProgress";

const ORCHID_DRIVE_PATIENT_CHART_DEFAULT_PAGE_SIZE = 100;
const ORCHID_DRIVE_NOTE_ATTACHMENT_DEFAULT_PAGE_SIZE = 100;
const ORCHID_DRIVE_MEMBER_DRIVE_DEFAULT_PAGE_SIZE = 100;

export function isDriveEntryAFile(entryId: string): boolean {
  return entryId.startsWith("od_f");
}

export function isDriveEntryADirectory(entryId: string): boolean {
  return entryId.startsWith("od_d");
}

export function getDriveEntryStorageDrive(entryId: string): StorageDrive {
  if (!entryId.startsWith("od_f") && !entryId.startsWith("od_d")) {
    throw new Error("invalid drive entry id");
  }
  switch (entryId[4]) {
    case "p":
      return "private-chat";
    case "g":
      return "group-chat";
    case "n":
      return "note-attachment";
    case "c":
      return "patient-chart";
    case "d":
      return "member-drive";
  }
  throw new Error("invalid drive entry id");
}

interface OrchidDriveContextProps {
  entriesNeedKeys: EntriesNeedKeysFunc;
  orchidDriveEntryKeys: OrchidDriveKeysByEntryId;
  updateAvailableDriveEntries: UpdateAvailableDriveEntriesFunc;

  orchidDriveUpdates: OrchidDriveData;
  orchidDriveError?: string;
  orchidDriveErrorByEntryId: DriveEntryErrorById;
  uploadTaskUpdates: Map<string, OrchidDriveUploadTask>;
  localUploadTaskStatus: LocalUploadTaskStatus[];
  downloadProgress: OrchidDriveDownloadProgress[];

  operations: {
    uploadFile: UploadFileGenericFunc;
    uploadFileMemberDrive: UploadFileMemberDriveFunc;
    uploadFilePatientChart: UploadFilePatientChartFunc;
    uploadFileNoteAttachment: UploadFileNoteAttachmentFunc;
    uploadFileContinue: UploadFileContinueFunc;
    uploadTaskAbort: UploadTaskAbortFunc;
    downloadFiles: DownloadFilesFunc;
    moveEntry: MoveEntryFunc;
    deleteEntry: DeleteEntryFunc;
    batchEntryOps: BatchEntryOpsFunc;
    createDirectory: CreateDirectoryFunc;
    createDirectoryMemberDrive: CreateDirectoryMemberDriveFunc;
    createDirectoryPatientChart: CreateDirectoryPatientChartFunc;
    createDirectoryNoteAttachment: CreateDirectoryNoteAttachmentFunc;
  };
}

export const OrchidDriveContext = createContext<OrchidDriveContextProps>(
  {} as OrchidDriveContextProps,
);

type Props = { children: ReactNode };

// TODO: rename to provider
export function OrchidDriveInternal({ children }: Props) {
  const usedFileDataIds: Set<string> = new Set();

  const {
    entriesNeedKeys,
    orchidDriveEntryKeys,
    updateAvailableDriveEntries,
    uploadTaskUpdates,
    localUploadTaskStatus,

    uploadFile,
    uploadFileMemberDrive,
    uploadFilePatientChart,
    uploadFileNoteAttachment,
    uploadFileContinue,
    uploadTaskAbort,
    downloadFiles,
    moveEntry,
    deleteEntry,
    batchEntryOps,
    createDirectory,
    createDirectoryMemberDrive,
    createDirectoryPatientChart,
    createDirectoryNoteAttachment,
    orchidDriveUpdates,
    orchidDriveError,
    orchidDriveErrorByEntryId,
  } = OrchidDriveProvider(usedFileDataIds);

  const downloadProgress = useDownloadProgress();

  return (
    <OrchidDriveContext.Provider
      value={{
        entriesNeedKeys,
        orchidDriveEntryKeys,
        updateAvailableDriveEntries,

        orchidDriveUpdates,
        orchidDriveError,
        orchidDriveErrorByEntryId,
        uploadTaskUpdates,
        localUploadTaskStatus,
        downloadProgress,

        operations: {
          uploadFile,
          uploadFileMemberDrive,
          uploadFilePatientChart,
          uploadFileNoteAttachment,
          uploadFileContinue,
          uploadTaskAbort,
          downloadFiles,
          moveEntry,
          deleteEntry,
          batchEntryOps,
          createDirectory,
          createDirectoryMemberDrive,
          createDirectoryPatientChart,
          createDirectoryNoteAttachment,
        },
      }}
    >
      {children}
    </OrchidDriveContext.Provider>
  );
}

export function useOrchidDriveProvider() {
  return useContext(OrchidDriveContext);
}

function processDriveSnapshotAndDispatchEntries(
  orchidDriveSnapshotResults: APIRequestResult,
  addPendingDriveEntries: AddPendingDriveEntriesFunc,
) {
  const files: APIOrchidDriveFile[] = [];
  const directories: APIOrchidDriveDirectory[] = [];
  for (const results of Object.values(orchidDriveSnapshotResults)) {
    if (!results.result) continue;
    files.push(results.result.files);
    directories.push(results.result.directories);
  }
  const fileEntries = ([] as APIOrchidDriveFile[]).concat(...files);
  const directoryEntries = ([] as APIOrchidDriveDirectory[]).concat(
    ...directories,
  );
  addPendingDriveEntries(fileEntries, directoryEntries);
}

function preparePageFromSnapshot(
  result: {
    files: APIOrchidDriveFile[];
    directories: APIOrchidDriveDirectory[];
  },
  orchidDriveFiles: OrchidDriveData,
): OrchidDriveData {
  const files = {};
  for (const e of result.files) {
    const entry = orchidDriveFiles.files[e.file_data_id];
    if (entry) files[entry.fileDataId] = entry;
  }

  const directories = {};
  for (const e of result.directories) {
    const entry = orchidDriveFiles.directories[e.directory_id];
    if (entry) directories[entry.directoryId] = entry;
  }

  return { files, directories };
}

function snapshotUpdateMayBeAvailable(
  orchidDriveSnapshotResults: APIRequestResult,
  orchidDriveUpdates: OrchidDriveData,
  directoryId: string | null | undefined,
  storageDrive: StorageDrive,
  patientUserId: string | undefined,
): number | null {
  const entriesInSnapshot: Map<
    string,
    {
      storage_drive: StorageDrive;
      parent_directory_id: null | string;
      patient_user_id: null | string;
      deleted_datetime: string;
    }
  > = new Map();
  let oldestSnapshotUpdate: number | null = null;
  for (const results of Object.values(orchidDriveSnapshotResults)) {
    if (!results.result?.last_modify_datetime) continue;
    const timestamp = new Date(results.result.last_modify_datetime).getTime();
    if (oldestSnapshotUpdate === null || oldestSnapshotUpdate > timestamp) {
      oldestSnapshotUpdate = timestamp;
    }

    for (const entry of results.result.files) {
      entriesInSnapshot.set(entry.file_data_id, entry);
    }
    for (const entry of results.result.directories) {
      entriesInSnapshot.set(entry.directory_id, entry);
    }
  }

  let latestRelevantUpdate: number | null = null;
  const fileAndDirectoryUpdates = [
    ...Object.values(orchidDriveUpdates.directories),
    ...Object.values(orchidDriveUpdates.files),
  ];
  for (const entry of fileAndDirectoryUpdates) {
    const entryId =
      "fileDataId" in entry ? entry.fileDataId : entry.directoryId;
    const snapshotEntry = entriesInSnapshot.get(entryId);

    const updatePresentInLastSnapshot =
      snapshotEntry !== undefined &&
      (directoryId === undefined ||
        (snapshotEntry.parent_directory_id === directoryId &&
          snapshotEntry.storage_drive === storageDrive &&
          (snapshotEntry.patient_user_id === patientUserId ||
            // special case when left is null right is undefined
            (!snapshotEntry.patient_user_id && !patientUserId))));

    const updateIsInTheScopeOfSnapshot =
      directoryId === undefined ||
      (entry.parentDirectoryId === directoryId &&
        entry.storageDrive === storageDrive &&
        entry.patientUserId === patientUserId);

    const entryTimestamp = entry.modifyDatetime.getTime();

    // file was possibly moved to here
    if (!updatePresentInLastSnapshot && updateIsInTheScopeOfSnapshot) {
      if (
        latestRelevantUpdate === null ||
        entryTimestamp > latestRelevantUpdate
      ) {
        latestRelevantUpdate = entryTimestamp;
      }
    }

    // file was possibly moved out from here
    if (updatePresentInLastSnapshot && !updateIsInTheScopeOfSnapshot) {
      if (
        latestRelevantUpdate === null ||
        entryTimestamp > latestRelevantUpdate
      ) {
        latestRelevantUpdate = entryTimestamp;
      }
    }

    // file was possibly deleted
    if (
      updateIsInTheScopeOfSnapshot &&
      updatePresentInLastSnapshot &&
      entry.deletedDatetime &&
      !snapshotEntry?.deleted_datetime
    ) {
      if (
        latestRelevantUpdate === null ||
        entryTimestamp > latestRelevantUpdate
      ) {
        latestRelevantUpdate = entryTimestamp;
      }
    }
  }

  if (oldestSnapshotUpdate === null) return latestRelevantUpdate;

  return latestRelevantUpdate && oldestSnapshotUpdate < latestRelevantUpdate
    ? latestRelevantUpdate
    : null;
}

function makeDirectoryFilterArg(directoryId?: string | null) {
  return directoryId === undefined
    ? `&list_all=true`
    : directoryId === null
    ? ""
    : `&directory_id=${directoryId}`;
}

export function usePatientChartsDrive({
  patientCharts,
  pageSize: pageSizeInput,
}: {
  patientCharts: UsedPatientChart[];
  pageSize?: number;
}) {
  const pageSize =
    pageSizeInput || ORCHID_DRIVE_PATIENT_CHART_DEFAULT_PAGE_SIZE;

  const orchidDrive = useOrchidDriveProvider();

  const patientChartsSet = useDeepEqualMemo(
    new Set(
      patientCharts.map((e) => {
        return {
          ...e,
          pages: new Set(e.pages),
        };
      }),
    ),
  );

  const { addPendingDriveEntries, orchidDriveFiles } = useOrchidDriveData(
    orchidDrive.entriesNeedKeys,
    orchidDrive.orchidDriveEntryKeys,
  );

  const snapshotRequests = useMemo(() => {
    const requests = {};
    for (const patientChart of patientChartsSet) {
      for (const page of patientChart.pages) {
        const skip = page * pageSize;
        requests[
          `patientChart_${pageSize}_${patientChart.patientUserId}_${patientChart.directoryId}_${page}`
        ] = {
          method: "GET",
          resource: `/api/drive_data/snapshot/storage/patient-chart/${
            patientChart.patientUserId
          }?limit=${pageSize}&skip=${skip}${makeDirectoryFilterArg(
            patientChart.directoryId,
          )}`,
        };
      }
    }
    return requests;
  }, [patientChartsSet, pageSize]);

  const [updateTimestamp, setUpdateTimestamp] = useState(0);
  const orchidDriveSnapshotResults = usePrivateAPIData(
    snapshotRequests,
    updateTimestamp,
  );

  useEffect(() => {
    let latestRelevantUpdate: number | null = null;
    for (const patientChart of patientChartsSet) {
      const snapshots = {};
      for (const [key, results] of Object.entries(orchidDriveSnapshotResults)) {
        if (
          key.startsWith(
            `patientChart_${pageSize}_${patientChart.patientUserId}_${patientChart.directoryId}_`,
          )
        ) {
          snapshots[key] = results;
        }
      }
      const updateTimestamp = snapshotUpdateMayBeAvailable(
        snapshots,
        orchidDrive.orchidDriveUpdates,
        patientChart.directoryId,
        "patient-chart",
        patientChart.patientUserId,
      );
      if (
        latestRelevantUpdate === null ||
        (updateTimestamp && latestRelevantUpdate < updateTimestamp)
      ) {
        latestRelevantUpdate = updateTimestamp;
      }
    }
    if (latestRelevantUpdate) {
      setUpdateTimestamp(latestRelevantUpdate);
    }
  }, [
    orchidDriveSnapshotResults,
    orchidDrive.orchidDriveUpdates,
    patientChartsSet,
    pageSize,
  ]);

  useEffect(
    () =>
      processDriveSnapshotAndDispatchEntries(
        orchidDriveSnapshotResults,
        addPendingDriveEntries,
      ),
    [orchidDriveSnapshotResults, addPendingDriveEntries],
  );

  const orchidMemberDriveError = useFetchAPIError(orchidDriveSnapshotResults);

  const orchidDriveFilesUpdated = useMergedOrchidDriveFiles(
    orchidDriveFiles,
    orchidDrive.orchidDriveUpdates,
  );

  const orchidDriveEntries = useMemo(() => {
    const entries: Array<{
      totalEntryCount;
      pages: {
        [name: string]: OrchidDriveData;
      };
    }> = [];

    for (const patientChart of patientChartsSet) {
      let totalEntryCount = 0;
      for (const page of patientChart.pages) {
        const resultKey = `patientChart_${pageSize}_${patientChart.patientUserId}_${patientChart.directoryId}_${page}`;
        const result = orchidDriveSnapshotResults[resultKey]?.result;
        if (!result) continue;
        if (result.total_count > totalEntryCount)
          totalEntryCount = result.total_count;
      }

      const pagesData = {};
      for (const page of patientChart.pages) {
        const resultKey = `patientChart_${pageSize}_${patientChart.patientUserId}_${patientChart.directoryId}_${page}`;
        const result = orchidDriveSnapshotResults[resultKey]?.result;
        if (!result) continue;
        pagesData[`${page}`] = preparePageFromSnapshot(
          result,
          orchidDriveFilesUpdated,
        );
      }

      entries.push({
        totalEntryCount,
        pages: pagesData,
      });
    }
    return entries;
  }, [
    orchidDriveSnapshotResults,
    patientChartsSet,
    pageSize,
    orchidDriveFilesUpdated,
  ]);

  return {
    ...orchidDrive.operations,
    downloadProgress: orchidDrive.downloadProgress,
    entries: orchidDriveEntries,
    orchidMemberDriveError:
      orchidMemberDriveError || orchidDrive.orchidDriveError,
    orchidDriveErrorByEntryIds: orchidDrive.orchidDriveErrorByEntryId,
  };
}

export function useNoteAttachments({
  noteAttachments,
  pageSize: pageSizeInput,
}: {
  noteAttachments: UsedNoteAttachment[];
  pageSize?: number;
}) {
  const pageSize =
    pageSizeInput || ORCHID_DRIVE_NOTE_ATTACHMENT_DEFAULT_PAGE_SIZE;

  const orchidDrive = useOrchidDriveProvider();
  const noteAttachmentsSet = useDeepEqualMemo(
    new Set(
      noteAttachments.map((e) => {
        return {
          ...e,
          pages: new Set(e.pages),
        };
      }),
    ),
  );

  const { addPendingDriveEntries, orchidDriveFiles } = useOrchidDriveData(
    orchidDrive.entriesNeedKeys,
    orchidDrive.orchidDriveEntryKeys,
  );

  const snapshotRequests = useMemo(() => {
    const requests = {};
    for (const noteAttachment of noteAttachmentsSet) {
      for (const page of noteAttachment.pages) {
        const skip = page * pageSize;
        requests[
          `noteAttachment_${pageSize}_${noteAttachment.patientUserId}_${noteAttachment.directoryId}_${page}`
        ] = {
          method: "GET",
          resource: `/api/drive_data/snapshot/storage/note-attachment/${
            noteAttachment.patientUserId
          }?limit=${pageSize}&skip=${skip}${makeDirectoryFilterArg(
            noteAttachment.directoryId,
          )}`,
        };
      }
    }
    return requests;
  }, [noteAttachmentsSet, pageSize]);

  const [updateTimestamp, setUpdateTimestamp] = useState(0);
  const orchidDriveSnapshotResults = usePrivateAPIData(
    snapshotRequests,
    updateTimestamp,
  );

  useEffect(() => {
    let latestRelevantUpdate: number | null = null;
    for (const noteAttachment of noteAttachmentsSet) {
      const snapshots = {};
      for (const [key, results] of Object.entries(orchidDriveSnapshotResults)) {
        if (
          key.startsWith(
            `noteAttachment_${pageSize}_${noteAttachment.patientUserId}_${noteAttachment.directoryId}_`,
          )
        ) {
          snapshots[key] = results;
        }
      }
      const updateTimestamp = snapshotUpdateMayBeAvailable(
        snapshots,
        orchidDrive.orchidDriveUpdates,
        noteAttachment.directoryId,
        "note-attachment",
        noteAttachment.patientUserId,
      );
      if (
        latestRelevantUpdate === null ||
        (updateTimestamp && latestRelevantUpdate < updateTimestamp)
      ) {
        latestRelevantUpdate = updateTimestamp;
      }
    }
    if (latestRelevantUpdate) {
      setUpdateTimestamp(latestRelevantUpdate);
    }
  }, [
    orchidDriveSnapshotResults,
    orchidDrive.orchidDriveUpdates,
    noteAttachmentsSet,
    pageSize,
  ]);

  useEffect(
    () =>
      processDriveSnapshotAndDispatchEntries(
        orchidDriveSnapshotResults,
        addPendingDriveEntries,
      ),
    [orchidDriveSnapshotResults, addPendingDriveEntries],
  );

  const orchidMemberDriveError = useFetchAPIError(orchidDriveSnapshotResults);

  const totalEntryCount = useMemo(() => {
    let totalEntryCount = 0;
    for (const patientChart of noteAttachmentsSet) {
      for (const page of patientChart.pages) {
        const resultKey = `noteAttachment_${pageSize}_${patientChart.patientUserId}_${patientChart.directoryId}_${page}`;
        const result = orchidDriveSnapshotResults[resultKey]?.result;
        if (!result) continue;
        if (result.total_count > totalEntryCount)
          totalEntryCount = result.total_count;
      }
    }
    return totalEntryCount;
  }, [orchidDriveSnapshotResults, noteAttachmentsSet, pageSize]);

  const orchidDriveFilesUpdated = useMergedOrchidDriveFiles(
    orchidDriveFiles,
    orchidDrive.orchidDriveUpdates,
  );

  const noteAttachmentsResults: { [name: string]: OrchidDriveData } =
    useMemo(() => {
      const pages = {};
      for (const patientChart of noteAttachmentsSet) {
        for (const page of patientChart.pages) {
          const resultKey = `noteAttachment_${pageSize}_${patientChart.patientUserId}_${patientChart.directoryId}_${page}`;
          const result = orchidDriveSnapshotResults[resultKey]?.result;
          if (!result) continue;
          pages[`${page}`] = preparePageFromSnapshot(
            result,
            orchidDriveFilesUpdated,
          );
        }
      }
      return pages;
    }, [
      orchidDriveSnapshotResults,
      noteAttachmentsSet,
      pageSize,
      orchidDriveFilesUpdated,
    ]);

  return {
    ...orchidDrive.operations,
    downloadProgress: orchidDrive.downloadProgress,
    entries: {
      totalEntryCount,
      pages: noteAttachmentsResults,
    },
    orchidMemberDriveError:
      orchidMemberDriveError || orchidDrive.orchidDriveError,
    orchidDriveErrorByEntryIds: orchidDrive.orchidDriveErrorByEntryId,
  };
}

export function useMemberDrive({
  memberDrive,
  pageSize: pageSizeInput,
}: {
  memberDrive: UsedMemberDrive[];
  pageSize?: number;
}) {
  const pageSize = pageSizeInput || ORCHID_DRIVE_MEMBER_DRIVE_DEFAULT_PAGE_SIZE;

  const orchidDrive = useOrchidDriveProvider();
  const memberDriveSet = useDeepEqualMemo(
    new Set(
      memberDrive.map((e) => {
        return {
          ...e,
          pages: new Set(e.pages),
        };
      }),
    ),
  );

  const { addPendingDriveEntries, orchidDriveFiles } = useOrchidDriveData(
    orchidDrive.entriesNeedKeys,
    orchidDrive.orchidDriveEntryKeys,
  );

  const snapshotRequests = useMemo(() => {
    const requests = {};
    for (const memberDrive of memberDriveSet) {
      for (const page of memberDrive.pages) {
        const skip = page * pageSize;
        requests[`memberDrive_${pageSize}_${memberDrive.directoryId}_${page}`] =
          {
            method: "GET",
            resource: `/api/drive_data/snapshot/storage/member-drive?limit=${pageSize}&skip=${skip}${makeDirectoryFilterArg(
              memberDrive.directoryId,
            )}`,
          };
      }
    }
    return requests;
  }, [memberDriveSet, pageSize]);

  const [updateTimestamp, setUpdateTimestamp] = useState(0);
  const orchidDriveSnapshotResults = usePrivateAPIData(
    snapshotRequests,
    updateTimestamp,
  );

  useEffect(() => {
    let latestRelevantUpdate: number | null = null;
    for (const memberDrive of memberDriveSet) {
      const snapshots = {};
      for (const [key, results] of Object.entries(orchidDriveSnapshotResults)) {
        if (
          key.startsWith(`memberDrive_${pageSize}_${memberDrive.directoryId}_`)
        ) {
          snapshots[key] = results;
        }
      }
      const updateTimestamp = snapshotUpdateMayBeAvailable(
        snapshots,
        orchidDrive.orchidDriveUpdates,
        memberDrive.directoryId,
        "member-drive",
        undefined,
      );
      if (
        latestRelevantUpdate === null ||
        (updateTimestamp && latestRelevantUpdate < updateTimestamp)
      ) {
        latestRelevantUpdate = updateTimestamp;
      }
    }
    if (latestRelevantUpdate) {
      setUpdateTimestamp(latestRelevantUpdate);
    }
  }, [
    orchidDriveSnapshotResults,
    orchidDrive.orchidDriveUpdates,
    memberDriveSet,
    pageSize,
  ]);

  useEffect(
    () =>
      processDriveSnapshotAndDispatchEntries(
        orchidDriveSnapshotResults,
        addPendingDriveEntries,
      ),
    [orchidDriveSnapshotResults, addPendingDriveEntries],
  );

  const orchidMemberDriveError = useFetchAPIError(orchidDriveSnapshotResults);

  const orchidDriveFilesUpdated = useMergedOrchidDriveFiles(
    orchidDriveFiles,
    orchidDrive.orchidDriveUpdates,
  );

  const orchidDriveEntries = useMemo(() => {
    const entries: Array<{
      totalEntryCount;
      pages: {
        [name: string]: OrchidDriveData;
      };
    }> = [];

    for (const memberDrive of memberDriveSet) {
      let totalEntryCount = 0;
      for (const page of memberDrive.pages) {
        const resultKey = `memberDrive_${pageSize}_${memberDrive.directoryId}_${page}`;
        const result = orchidDriveSnapshotResults[resultKey]?.result;
        if (!result) continue;
        if (result.total_count > totalEntryCount)
          totalEntryCount = result.total_count;
      }

      const pagesData = {};
      for (const page of memberDrive.pages) {
        const resultKey = `memberDrive_${pageSize}_${memberDrive.directoryId}_${page}`;
        const result = orchidDriveSnapshotResults[resultKey]?.result;
        if (!result) continue;
        pagesData[`${page}`] = preparePageFromSnapshot(
          result,
          orchidDriveFilesUpdated,
        );
      }

      entries.push({
        totalEntryCount,
        pages: pagesData,
      });
    }
    return entries;
  }, [
    orchidDriveSnapshotResults,
    pageSize,
    memberDriveSet,
    orchidDriveFilesUpdated,
  ]);

  return {
    ...orchidDrive.operations,
    downloadProgress: orchidDrive.downloadProgress,
    entries: orchidDriveEntries,
    orchidMemberDriveError:
      orchidMemberDriveError || orchidDrive.orchidDriveError,
    orchidDriveErrorByEntryIds: orchidDrive.orchidDriveErrorByEntryId,
  };
}

export function useOrchidDrive({ entryIds }: { entryIds: string[] }) {
  const orchidDrive = useOrchidDriveProvider();
  const entryIdsSet = useDeepEqualMemo(new Set(entryIds));

  const { addPendingDriveEntries, orchidDriveFiles } = useOrchidDriveData(
    orchidDrive.entriesNeedKeys,
    orchidDrive.orchidDriveEntryKeys,
  );

  const snapshotRequests = useMemo(() => {
    const requests: APIRequests = {};
    if (entryIdsSet.size > 0) {
      // TODO: we should do paging
      requests["fileDataIds"] = {
        resource: `/api/drive_data/snapshot/storage`,
        fetchArgs: {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ entry_ids: Array.from(entryIdsSet) }),
        },
      };
    }

    return requests;
  }, [entryIdsSet]);

  const orchidDriveSnapshotResults = usePrivateAPIData(snapshotRequests, 0);

  useEffect(() => {
    const files = [];
    const directories = [];
    for (const results of Object.values(orchidDriveSnapshotResults)) {
      if (!results.result) continue;
      files.concat(results.result.files);
      directories.concat(results.result.directories);
    }
    addPendingDriveEntries(files, directories);
  }, [orchidDriveSnapshotResults, addPendingDriveEntries]);

  const orchidMemberDriveError = useFetchAPIError(orchidDriveSnapshotResults);

  const orchidDriveErrorByEntryIds = useMemo(() => {
    const errorsByEntryId = {};
    for (const results of Object.values(orchidDriveSnapshotResults)) {
      if (!results.result) continue;
      for (const failedEntry of results.result.failed_entries) {
        errorsByEntryId[failedEntry.entry_id] = failedEntry.error;
      }
    }
    return errorsByEntryId;
  }, [orchidDriveSnapshotResults]);

  return {
    ...orchidDrive.operations,
    downloadProgress: orchidDrive.downloadProgress,
    orchidDriveFiles: useMergedOrchidDriveFiles(
      orchidDriveFiles,
      orchidDrive.orchidDriveUpdates,
    ),
    orchidMemberDriveError:
      orchidMemberDriveError || orchidDrive.orchidDriveError,
    orchidDriveErrorByEntryIds: useMergedDriveErrorByEntryIds(
      orchidDriveErrorByEntryIds,
      orchidDrive.orchidDriveErrorByEntryId,
    ),
  };
}

type UploadTaskStatus = {
  uploadTask?: OrchidDriveUploadTaskWithEntries;
  localUploadTask?: LocalUploadTaskStatus;
};

function mergeUploadTasksWithLocalUploadTasks(
  uploadTasks: OrchidDriveUploadTaskWithEntries[],
  localUploadTaskStatuses: LocalUploadTaskStatus[],
): UploadTaskStatus[] {
  const uploadTasksByUploadTaskId = new Map();
  for (const uploadTask of uploadTasks) {
    uploadTasksByUploadTaskId.set(uploadTask.uploadTaskId, uploadTask);
  }
  const localUploadTasks: Set<string> = new Set();
  const results: UploadTaskStatus[] = [];
  for (const localUploadTask of localUploadTaskStatuses) {
    if ("uploadTaskId" in localUploadTask && localUploadTask.uploadTaskId) {
      localUploadTasks.add(localUploadTask.uploadTaskId);
    }
    results.push({
      uploadTask:
        "uploadTaskId" in localUploadTask
          ? uploadTasksByUploadTaskId.get(localUploadTask.uploadTaskId)
          : undefined,
      localUploadTask,
    });
  }
  return [
    ...results,
    ...uploadTasks
      .filter((e) => !localUploadTasks.has(e.uploadTaskId))
      .map((uploadTask) => {
        return { uploadTask };
      }),
  ];
}

export function useOrchidDriveUploadTasks() {
  const orchidDrive = useOrchidDriveProvider();
  const { uploadTaskUpdates, localUploadTaskStatus } = orchidDrive;

  const {
    uploadTasks: {
      result: uploadTasksSnapshotResult,
      errorText: uploadTasksError,
    },
  } = usePrivateAPIData(
    {
      uploadTasks: {
        resource: `/api/drive_data/snapshot/storage/upload-tasks`,
        fetchArgs: {
          method: "GET",
        },
      },
    },
    0,
  );

  const uploadTasksSnapshot: OrchidDriveUploadTask[] = useMemo(() => {
    if (!uploadTasksSnapshotResult) return [];
    return uploadTasksSnapshotResult.map(orchidDriveUploadTaskFromAPI);
  }, [uploadTasksSnapshotResult]);

  const entriesInUploadTasks: string[] = useMemo(() => {
    const referencedEntryIds: string[][] = [
      ...uploadTasksSnapshot.map((e) => e.fileIds.concat(e.directoryIds)),
      ...Array.from(uploadTaskUpdates.values()).map((e) =>
        e.fileIds.concat(e.directoryIds),
      ),
    ];
    return Array.from(new Set(([] as string[]).concat(...referencedEntryIds)));
  }, [uploadTasksSnapshot, uploadTaskUpdates]);

  const {
    orchidDriveFiles,
    orchidMemberDriveError,
    orchidDriveErrorByEntryIds,
  } = useOrchidDrive({ entryIds: entriesInUploadTasks });

  const uploadTasks: UploadTaskStatus[] = useMemo(() => {
    const uploadTasksById: Map<string, OrchidDriveUploadTask> = new Map(
      uploadTaskUpdates,
    );
    for (const uploadTask of uploadTasksSnapshot) {
      const task = uploadTasksById.get(uploadTask.uploadTaskId);
      if (
        !task ||
        task.modifyDatetime.getTime() < uploadTask.modifyDatetime.getTime()
      ) {
        uploadTasksById.set(uploadTask.uploadTaskId, uploadTask);
      }
    }
    const uploadTasks: OrchidDriveUploadTaskWithEntries[] = Array.from(
      uploadTasksById.values(),
    ).map((u) => {
      const files = u.fileIds
        .map((fileDataId) => orchidDriveFiles.files[fileDataId])
        .filter((e) => e !== undefined);
      const directories = u.directoryIds
        .map((directoryId) => orchidDriveFiles.directories[directoryId])
        .filter((e) => e !== undefined);

      const totalUploadedSize = files.reduce(
        (accu, e) => accu + e.chunkSize * ((e.lastUploadedChunkNum || 0) + 1),
        0,
      );

      return {
        ...u,
        files,
        directories,
        uploadPercent: u.totalExpectedSize
          ? Math.min(100, (totalUploadedSize * 100) / u.totalExpectedSize)
          : undefined,
      };
    });

    return mergeUploadTasksWithLocalUploadTasks(
      uploadTasks,
      localUploadTaskStatus,
    );
  }, [
    uploadTasksSnapshot,
    uploadTaskUpdates,
    localUploadTaskStatus,
    orchidDriveFiles,
  ]);

  return {
    ...orchidDrive.operations,
    uploadTasks,
    uploadTasksError: uploadTasksError || orchidMemberDriveError,
    orchidDriveErrorByEntryIds,
  };
}
